import useStepWithNext from 'components/StepLine/context/hooks/useStepWithNext'
import StepLineModal from 'components/StepLine/StepLineModal'
import { useConnection } from 'hooks/useConnection'
import React, { useCallback, useEffect, useState } from 'react'
import { ThemedText } from 'theme'
import { isAddress } from 'utils'

import { useRewards } from '../../context/RewardContext'
import { useRewardVaultClaim } from '../../hooks/useRewardVaultClaim'
import RewardVaultClaimStep from '../steps/claim/RewardVaultClaimStep'
import RewardVaultClaimModal from './RewardVaultClaimModal'

const VaultClaimModal: React.FC = () => {
  const { account } = useConnection()
  const [isResetting, setIsResetting] = useState(false)
  const [sessionId, setSessionId] = useState(Date.now())
  const [modalKey, setModalKey] = useState(0)
  const [phase, setPhase] = useState('selection')

  const {
    activeModal,
    closeModal,
    rewardVaultEarned,
    rewardVaultPreferences,
    updateVaultPreferences,
    setClaimTxState,
    rewardVaultRewardToken,
    rewardVault,
  } = useRewards()

  const { claimRewards, claimState, resetTxStates } = useRewardVaultClaim(rewardVault?.vaultAddress || undefined)

  useEffect(() => {
    if (activeModal === 'vaultClaim') {
      resetTxStates()
    }
  }, [activeModal, resetTxStates])

  useEffect(() => {
    if (activeModal === 'vaultClaim') {
      setSessionId(Date.now())
      resetTxStates()
      setModalKey((prev) => prev + 1)
    }
  }, [activeModal, resetTxStates])

  useEffect(() => {
    return () => {
      resetTxStates()
    }
  }, [resetTxStates])

  useEffect(() => {
    setClaimTxState({
      hash: claimState.hash,
      attemptingTx: claimState.attemptingTx,
      error: claimState.error,
    })
  }, [claimState, setClaimTxState])

  useEffect(() => {
    if (activeModal !== 'vaultClaim') {
      setPhase('selection')
    }
  }, [activeModal])

  const handleSelectionConfirm = async () => {
    setIsResetting(true)
    setSessionId(Date.now())

    await new Promise((resolve) => setTimeout(resolve, 50))

    setIsResetting(false)
    setPhase('transaction')
    setModalKey((prev) => prev + 1)
  }

  const handleClaimStep = async () => {
    if (!account || !rewardVaultEarned.length || !rewardVaultRewardToken) return null

    try {
      const recipient =
        rewardVaultPreferences.claimDestination === 'connected' ? account : rewardVaultPreferences.otherWalletAddress

      if (!isAddress(recipient)) {
        throw new Error('Invalid Ethereum address')
      }

      const amount = rewardVaultEarned[0].amount?.quotient.toString() || '0'
      const rewardTokenSymbol = rewardVaultEarned[0].amount?.currency.symbol || rewardVaultRewardToken.symbol
      const success = await claimRewards(recipient, amount, rewardTokenSymbol)

      return success ? true : null
    } catch (error: any) {
      console.error('Error in claim step:', error)
      return null
    }
  }

  const setClaimDestination = useCallback(
    (destination: 'connected' | 'other') => updateVaultPreferences({ claimDestination: destination }),
    [updateVaultPreferences]
  )

  const setOtherWalletAddress = useCallback(
    (address: string) => updateVaultPreferences({ otherWalletAddress: address }),
    [updateVaultPreferences]
  )

  const rewardVaultRewardSymbol = rewardVaultEarned[0]?.amount?.currency.symbol || rewardVaultRewardToken?.symbol
  const claimActionText = rewardVaultRewardSymbol ? `Claim ${rewardVaultRewardSymbol}` : 'Claim Rewards'
  const modalTitle = rewardVaultRewardSymbol ? `Claim ${rewardVaultRewardSymbol} Rewards` : 'Claim Rewards'

  const claimStep = useStepWithNext(`claim-${sessionId}`, RewardVaultClaimStep, {
    earnedAmount: rewardVaultEarned[0]?.amount,
    account,
    claimDestination: rewardVaultPreferences.claimDestination,
    otherWalletAddress: rewardVaultPreferences.otherWalletAddress,
    onClaim: handleClaimStep,
    attemptingTxn: claimState.attemptingTx,
    hash: claimState.hash,
    error: claimState.error,
    pendingText: claimActionText,
    autoConfirm: true,
    transactionWaiting: true,
  })

  const getSteps = useCallback(() => {
    return [claimStep]
  }, [claimStep])

  const handleModalClose = () => {
    closeModal()

    setTimeout(() => {
      resetTxStates()
      setPhase('selection')
    }, 50)
  }

  if (activeModal !== 'vaultClaim') return null

  const modalHeader = <ThemedText.DeprecatedMediumHeader>{modalTitle}</ThemedText.DeprecatedMediumHeader>

  return (
    <>
      {phase === 'selection' && (
        <RewardVaultClaimModal
          key={`selection-${modalKey}`}
          isOpen={activeModal === 'vaultClaim'}
          onDismiss={closeModal}
          onConfirm={handleSelectionConfirm}
          earnedAmount={rewardVaultEarned[0]?.amount}
          account={account}
          claimDestination={rewardVaultPreferences.claimDestination}
          setClaimDestination={setClaimDestination}
          otherWalletAddress={rewardVaultPreferences.otherWalletAddress}
          setOtherWalletAddress={setOtherWalletAddress}
        />
      )}
      {phase === 'transaction' && !isResetting && (
        <StepLineModal
          key={`transaction-${modalKey}`}
          isOpen={true}
          onDismiss={handleModalClose}
          steps={getSteps()}
          header={modalHeader}
          onFinish={handleModalClose}
        />
      )}
    </>
  )
}

export default VaultClaimModal
